import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Warning, ArrowClockwise, X } from "@phosphor-icons/react";

/**
 * Sticky banner shown on every page when the logged-in user hasn't yet
 * verified their email. Drops away the moment they click the magic link
 * (next /auth/me poll picks it up). User can dismiss for the session, but
 * it reappears on next page load until verified.
 */
export default function UnverifiedEmailBanner() {
    const { user, setUser } = useAuth();
    const [dismissed, setDismissed] = useState(
        () => sessionStorage.getItem("cf-unverified-banner-dismissed") === "1"
    );
    const [resending, setResending] = useState(false);
    const [sent, setSent] = useState(false);

    if (!user || user.email_verified || dismissed) return null;

    // Demo accounts use a throwaway alias (demo+xxxx@charityfundraiser.com) that
    // nobody can actually verify, so for demos we show a friendly placeholder
    // email and explain that this is just the verification step — close to proceed.
    const isDemo = !!user.is_demo;
    const displayEmail = isDemo ? "admin@youremailaddress.com" : user.email;

    const resend = async () => {
        setResending(true);
        try {
            await api.post("/email/verification/resend", {
                email: user.email, name: user.name || "", purpose: "signup",
            });
            setSent(true);
            setTimeout(() => setSent(false), 5000);
        } catch (e) {
            // best-effort UI signal only
            setSent(false);
        } finally {
            setResending(false);
        }
    };

    const checkAgain = async () => {
        try {
            const r = await api.get("/auth/me");
            setUser(r.data);
        } catch { /* noop */ }
    };

    const dismiss = () => {
        sessionStorage.setItem("cf-unverified-banner-dismissed", "1");
        setDismissed(true);
    };

    return (
        <div className="bg-amber-50 border-b-2 border-amber-500 text-amber-900 px-4 py-2.5 text-sm flex items-center justify-between flex-wrap gap-2" data-testid="unverified-email-banner">
            <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                <Warning size={16} className="text-amber-700 shrink-0"/>
                <span>
                    {isDemo
                        ? <>This banner is part of the email‑verification step — on a live account you'd confirm <strong>{displayEmail}</strong> here to receive receipts &amp; invoices without bouncing. For this demo, just click <strong>Close</strong>.</>
                        : sent
                            ? <>A fresh verification link was just sent to <strong>{displayEmail}</strong>. Click it from your inbox.</>
                            : <>Please verify <strong>{displayEmail}</strong> to receive receipts, invoices, and event updates without bouncing.</>}
                </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {isDemo ? (
                    <button type="button" onClick={dismiss}
                            className="border border-amber-700 text-amber-900 px-3 py-1.5 text-xs uppercase tracking-wider font-bold flex items-center gap-1"
                            data-testid="dismiss-verification-btn">
                        <X size={12}/> Close
                    </button>
                ) : (
                    <>
                        <button type="button" onClick={resend} disabled={resending}
                                className="border border-amber-700 text-amber-900 px-3 py-1.5 text-xs uppercase tracking-wider font-bold flex items-center gap-1 disabled:opacity-50"
                                data-testid="resend-verification-btn">
                            <ArrowClockwise size={12}/> {resending ? "Sending…" : "Resend link"}
                        </button>
                        <button type="button" onClick={checkAgain}
                                className="text-xs underline font-bold"
                                data-testid="recheck-verification-btn">
                            I've clicked it — re-check
                        </button>
                        <button type="button" onClick={dismiss} aria-label="Dismiss"
                                className="text-amber-700 hover:text-amber-900"
                                data-testid="dismiss-verification-btn">
                            <X size={14}/>
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
